class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = 'bca-20260930-' + searchTerm.trim().toLocaleLowerCase();
    const liveCatalog = window.BCALiveCatalog;
    const catalogReady = liveCatalog ? liveCatalog.refresh().catch(() => null) : Promise.resolve(null);
    this.abortController.abort();
    this.abortController = new AbortController();
    const controller = this.abortController;
    const signal = controller.signal;
    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 6000);
    this.setLiveRegionLoadingState();
    let liveRendered = false;
    const renderLive = () => {
      if (!liveCatalog || this.getQuery() !== searchTerm || this.abortController !== controller) return false;
      const matches = liveCatalog.matches(searchTerm);
      if (!matches.length) return false;
      const previous = new DOMParser().parseFromString(this.predictiveSearchResults.innerHTML || '', 'text/html');
      const styles = Array.from(previous.querySelectorAll('style')).map(style => style.outerHTML).join('');
      this.renderSearchResults(liveCatalog.predictiveMarkup(matches,searchTerm,styles));
      liveRendered = true;
      controller.abort();
      return true;
    };
    const unsubscribeLive = liveCatalog?.subscribe ? liveCatalog.subscribe(renderLive) : () => {};
    const liveTask = catalogReady.then(renderLive);
    try {
      const cached = this.cachedResults[queryKey];
      if (cached && Date.now() - cached.at < 30000) {
        this.renderSearchResults(cached.markup);
        await liveTask;
        return;
      }
      const params = new URLSearchParams({q:searchTerm,section_id:'predictive-search','resources[type]':'product','resources[limit]':'10','resources[options][unavailable_products]':'last','resources[options][fields]':'title,product_type,variants.title,vendor'});
      const load = async query => {
        params.set('q',query);
        const response = await fetch(routes.predictive_search_url+'?'+params, {signal,headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('SEARCH_UNAVAILABLE');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        if (!doc.querySelector('#shopify-section-predictive-search')) throw new Error('SEARCH_MARKUP_MISSING');
        return doc;
      };
      let doc = await load(searchTerm);
      if (liveRendered || signal.aborted || this.getQuery() !== searchTerm) return;
      let effectiveQuery = searchTerm;
      // The current Shopify catalogue is authoritative. Correct spelling only
      // when the original request found nothing; never replace a valid name.
      if (!doc.querySelector('a[href*="/products/"]')) {
        const corrected = window.BCASmartSearch?.resolveQuery(searchTerm);
        if (corrected?.changed && corrected.query !== corrected.raw) {
          const fallback = await load(corrected.query);
          if (fallback.querySelector('a[href*="/products/"]')) {
            doc = fallback;
            effectiveQuery = corrected.query;
            const note=doc.createElement('p'); note.className='bac-ps-notice-text';
            note.textContent='Resultados cercanos para “'+corrected.query+'”.';
            doc.querySelector('#predictive-search-results')?.prepend(note);
          }
        }
      }
      if (signal.aborted || this.getQuery() !== searchTerm) return;
      const productRows = Array.from(doc.querySelectorAll('[data-bac-searchable]'));
      const titleMatches = window.BCASmartSearch?.titleMatchesQuery;
      if (titleMatches && effectiveQuery === searchTerm && !productRows.some(row => titleMatches(row.querySelector('.bac-ps-product-title')?.textContent || '', searchTerm))) {
        const correction = window.BCASmartSearch?.resolveQuery(searchTerm);
        if (correction?.changed && productRows.some(row => titleMatches(row.querySelector('.bac-ps-product-title')?.textContent || '',correction.query))) {
          effectiveQuery = correction.query;
          const note=doc.createElement('p'); note.className='bac-ps-notice-text';
          note.textContent='Resultados cercanos para “'+correction.query+'”.';
          doc.querySelector('#predictive-search-results')?.prepend(note);
        }
      }
      const exactRows = titleMatches ? productRows.filter(row => titleMatches(row.querySelector('.bac-ps-product-title')?.textContent || '',effectiveQuery)) : [];
      if (exactRows.length && exactRows.length < productRows.length) {
        productRows.forEach(row => { if (!exactRows.includes(row)) row.remove(); });
        const heading = doc.querySelector('.bac-ps-results-title');
        if (heading) heading.textContent = 'Sugerencias de productos ('+exactRows.length+')';
        const count = doc.querySelector('[data-total-results]');
        if (count) { count.dataset.totalResults = exactRows.length; count.textContent = exactRows.length+' productos encontrados'; }
      }
      const markup=doc.querySelector('#shopify-section-predictive-search').innerHTML;
      this.allPredictiveSearchInstances.forEach(instance=>{
        if (Object.keys(instance.cachedResults).length >= 40) instance.cachedResults = {};
        instance.cachedResults[queryKey]={markup,at:Date.now()};
      });
      this.renderSearchResults(markup);
      await liveTask;
    } catch (error) {
      await liveTask;
      if (liveRendered) return;
      if ((signal.aborted && !timedOut) || this.getQuery() !== searchTerm) return;
      const link=document.createElement('a');
      link.className='bac-ps-search-link';
      link.href=routes.search_url+'?'+new URLSearchParams({q:searchTerm,type:'product','options[prefix]':'last'});
      link.textContent='Ver todos los resultados de “'+searchTerm+'”';
      this.predictiveSearchResults.replaceChildren(link);
      this.setAttribute('results', true);
      this.setLiveRegionText('Puedes abrir la búsqueda completa.');
      this.open();
    } finally {
      unsubscribeLive();
      clearTimeout(timeout);
      if (this.abortController === controller) this.removeAttribute('loading');
      searchDeferred?.resolve({totalCount:this.getTotalResultCount()});
    }
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  getPrefixSearchTerm(searchTerm) {
    const terms = searchTerm.trim().replace(/\s+/g, ' ').split(' ');
    const lastTerm = terms.pop() || '';
    terms.push(`${lastTerm.replace(/\*+$/g, '')}*`);
    return terms.join(' ');
  }

  areSearchResultsRelevant(resultsDocument, searchTerm) {
    const resultsRoot = resultsDocument.querySelector('#predictive-search-results');
    if (!resultsRoot) return false;

    const serverSearchTerm = resultsRoot.dataset.bacSearchTerm;
    const searchTokens = this.normalizeSearchTerm(searchTerm).split(/\s+/).filter(Boolean);
    const products = Array.from(resultsDocument.querySelectorAll('[data-bac-searchable]'));
    if (!products.length) {
      return Boolean(
        serverSearchTerm && this.normalizeSearchTerm(serverSearchTerm) === this.normalizeSearchTerm(searchTerm)
      );
    }

    return products.every((product) => {
      const searchableText = this.normalizeSearchTerm(product.dataset.bacSearchable || '');
      return searchTokens.every((token) => searchableText.includes(token));
    });
  }

  getFilteredPreviousResultsDocument(resultsMarkup, searchTerm) {
    if (!resultsMarkup) return null;

    const resultsDocument = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const resultsRoot = resultsDocument.querySelector('#predictive-search-results');
    const previousSearchTerm = resultsRoot?.dataset.bacSearchTerm;
    if (
      !previousSearchTerm ||
      !this.normalizeSearchTerm(searchTerm).startsWith(this.normalizeSearchTerm(previousSearchTerm))
    ) {
      return null;
    }

    const searchTokens = this.normalizeSearchTerm(searchTerm).split(/\s+/).filter(Boolean);
    const products = Array.from(resultsDocument.querySelectorAll('[data-bac-searchable]'));
    const matchingProducts = products.filter((product) => {
      const searchableText = this.normalizeSearchTerm(product.dataset.bacSearchable || '');
      return searchTokens.every((token) => searchableText.includes(token));
    });

    if (!matchingProducts.length) return null;

    products.forEach((product) => {
      if (!matchingProducts.includes(product)) product.remove();
    });

    resultsRoot.dataset.bacSearchTerm = searchTerm;
    const resultsTitle = resultsDocument.querySelector('.bac-ps-results-title');
    if (resultsTitle) resultsTitle.textContent = `Sugerencias de productos (${matchingProducts.length})`;

    const allResultsLink = resultsDocument.querySelector('.bac-ps-search-link');
    if (allResultsLink) {
      allResultsLink.href = `${routes.search_url}?q=${encodeURIComponent(this.getPrefixSearchTerm(searchTerm))}&options%5Bprefix%5D=last`;
      allResultsLink.textContent = `Ver todos los resultados de “${searchTerm}” `;
      const linkArrow = resultsDocument.createElement('span');
      linkArrow.setAttribute('aria-hidden', 'true');
      linkArrow.textContent = '→';
      allResultsLink.append(linkArrow);
    }

    const nativeSearchText = resultsDocument.querySelector('[data-predictive-search-search-for-text]');
    if (nativeSearchText) nativeSearchText.textContent = `Buscar “${searchTerm}”`;

    const liveRegion = resultsDocument.querySelector('[data-total-results]');
    if (liveRegion) liveRegion.dataset.totalResults = matchingProducts.length.toString();

    return resultsDocument;
  }

  normalizeSearchTerm(searchTerm) {
    return searchTerm
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase();
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Request identity is checked before rendering. Typo-tolerant results
    // must not be rejected by requiring every title to contain the raw typo.

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || 'Resultados de búsqueda');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
